import { ErrorDeApi, pedir } from './api';
import {
  codigoPendiente,
  correoInterno,
  esCorreo,
  normalizarUsuario,
  problemaDelUsuario,
} from './cuenta-reglas';
import { useAcceso } from './acceso';
import { db } from './db';
import { getDeviceId } from './session';
import { arrancar, desvincularCuenta, tieneDatosDeOtraCuenta, vincularCuenta } from './sync';

/**
 * LA CUENTA, QUE YA ES OBLIGATORIA (spec 2026-10-09 «cuenta obligatoria»).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Antes la app entera vivía en el dispositivo y la cuenta era opcional. El precio
 * era que quien perdía o cambiaba de móvil sin cuenta lo perdía todo. Ahora no se
 * entra sin cuenta, y la cuenta es de **usuario y contraseña**, sin correo: si se
 * olvida la contraseña, el **código de recuperación** que se guardó al crearla pone
 * una nueva desde cualquier dispositivo.
 *
 * Lo que no cambia: ya dentro, la app funciona sin conexión, y lo que existía en el
 * dispositivo antes de tener cuenta **no se pierde ni se duplica** al vincular — los
 * identificadores son deterministas y vincular es encolar lo que ya hay.
 * ───────────────────────────────────────────────────────────────────────────
 */

export type Sesion = { userId: string; email: string; nombre: string };

type RespuestaSesion = {
  user?: { id?: string; email?: string; name?: string } | null;
} | null;

export async function sesionActual(): Promise<Sesion | null> {
  try {
    const respuesta = await pedir<RespuestaSesion>('/auth/get-session', {
      deviceId: getDeviceId(),
    });
    const usuario = respuesta?.user;
    if (!usuario?.id) return null;
    return { userId: usuario.id, email: usuario.email ?? '', nombre: usuario.name ?? '' };
  } catch {
    // Sin red o sin servidor: no hay sesión que resolver, y la app sigue igual.
    return null;
  }
}

export type ResultadoCuenta =
  { ok: true; sesion: Sesion } | { ok: false; mensaje: string; otraCuenta?: true };

export async function crearCuenta(args: {
  usuario: string;
  password: string;
  nombre: string;
}): Promise<ResultadoCuenta> {
  const problema = problemaDelUsuario(args.usuario);
  if (problema) return { ok: false, mensaje: problema };
  return intentar('/auth/sign-up/email', {
    email: correoInterno(args.usuario),
    password: args.password,
    name: args.nombre.trim() || args.usuario.trim(),
    // Tal cual lo escribió: Better Auth guarda la forma normalizada y la que se enseña.
    username: args.usuario.trim(),
  });
}

/** Con usuario, o con correo si es una cuenta de antes. */
export async function entrar(args: {
  identificador: string;
  password: string;
}): Promise<ResultadoCuenta> {
  const id = args.identificador.trim();
  return esCorreo(id)
    ? intentar('/auth/sign-in/email', { email: id, password: args.password })
    : intentar('/auth/sign-in/username', { username: id, password: args.password });
}

export type ResultadoRecuperar =
  { ok: true; sesion: Sesion; codigo: string } | { ok: false; mensaje: string; otraCuenta?: true };

/**
 * Contraseña olvidada: usuario + código → contraseña nueva, y se entra con ella. El
 * servidor devuelve un código nuevo (el usado ya no sirve), que hay que enseñar.
 */
export async function recuperar(args: {
  usuario: string;
  codigo: string;
  password: string;
}): Promise<ResultadoRecuperar> {
  let codigo: string;
  try {
    const respuesta = await pedir<{ codigo: string }>('/cuenta/recuperar', {
      deviceId: getDeviceId(),
      metodo: 'POST',
      cuerpo: {
        usuario: normalizarUsuario(args.usuario),
        codigo: args.codigo,
        password: args.password,
      },
    });
    codigo = respuesta.codigo;
  } catch (error) {
    return { ok: false, mensaje: mensajeDeError(error, 'recuperar') };
  }
  const resultado = await entrar({ identificador: args.usuario, password: args.password });
  return resultado.ok ? { ...resultado, codigo } : resultado;
}

export type EstadoRecuperacion = { tieneCodigo: boolean; username: string | null };

export async function estadoRecuperacion(): Promise<EstadoRecuperacion | null> {
  try {
    return await pedir<EstadoRecuperacion>('/cuenta/recuperacion', { deviceId: getDeviceId() });
  } catch {
    return null;
  }
}

/** Genera un código nuevo: el anterior deja de servir. Se enseña una sola vez. */
export async function nuevoCodigo(): Promise<string> {
  const respuesta = await pedir<{ codigo: string }>('/cuenta/recuperacion/codigo', {
    deviceId: getDeviceId(),
    metodo: 'POST',
    cuerpo: {},
  });
  return respuesta.codigo;
}

let codigoComprobado = false;

/**
 * Las cuentas de antes no tienen código de recuperación (ni, a veces, usuario). Al
 * abrir la app o al entrar se mira una vez por carga y, si falta, se genera y se
 * enseña: sin él, quien olvide la contraseña al cambiar de móvil se queda fuera.
 */
export async function pedirCodigoSiFalta(): Promise<void> {
  if (codigoComprobado) return;
  codigoComprobado = true;
  const pendiente = codigoPendiente(await estadoRecuperacion());
  if (!pendiente) return;
  try {
    useAcceso.getState().mostrarCodigo(await nuevoCodigo(), pendiente.pedirUsuario);
  } catch {
    // Sin red justo ahora: se vuelve a mirar en la próxima carga.
    codigoComprobado = false;
  }
}

/** Para las cuentas de antes, que entraban con correo y no tienen usuario. */
export async function elegirUsuario(usuario: string): Promise<string | null> {
  const problema = problemaDelUsuario(usuario);
  if (problema) return problema;
  try {
    await pedir('/auth/update-user', {
      deviceId: getDeviceId(),
      metodo: 'POST',
      cuerpo: { username: usuario.trim() },
    });
    return null;
  } catch (error) {
    return mensajeDeError(error);
  }
}

async function intentar(ruta: string, cuerpo: unknown): Promise<ResultadoCuenta> {
  try {
    await pedir(ruta, { deviceId: getDeviceId(), metodo: 'POST', cuerpo });
  } catch (error) {
    return { ok: false, mensaje: mensajeDeError(error) };
  }

  const sesion = await sesionActual();
  if (!sesion)
    return { ok: false, mensaje: 'La sesión no quedó abierta. Prueba a entrar otra vez.' };

  if (await tieneDatosDeOtraCuenta(sesion.userId)) {
    await cerrarSesionDelServidor();
    return {
      ok: false,
      otraCuenta: true,
      mensaje:
        'Este dispositivo guarda lo de otra cuenta y se mezclaría con esta. Para entrar con ella, borra antes los datos de este dispositivo.',
    };
  }

  /**
   * Vincular arranca el motor y, con él, el barrido de lo que ya había en local.
   * Va después de confirmar la sesión y no antes: encolar sin sesión dejaría la
   * cola llena de mutaciones que se rechazarían con 401 hasta morir.
   */
  await vincularCuenta(sesion.userId);
  return { ok: true, sesion };
}

export async function salir(): Promise<void> {
  // Antes del sign-out, que la baja necesita la sesión: si no, en un dispositivo
  // compartido seguían llegando los recordatorios de la cuenta cerrada.
  try {
    const { desactivarPush } = await import('./push');
    await desactivarPush();
  } catch {
    // Sin push activado o sin red: no hay nada que dar de baja ahora mismo.
  }
  await cerrarSesionDelServidor();
  await desvincularCuenta();
}

async function cerrarSesionDelServidor(): Promise<void> {
  try {
    await pedir('/auth/sign-out', { deviceId: getDeviceId(), metodo: 'POST', cuerpo: {} });
  } catch {
    // Si no hay red, se cierra igual en local: el usuario pidió salir.
  }
}

/**
 * Reanuda al abrir la app. Si hay sesión de servidor pero este dispositivo no
 * tenía la cuenta vinculada —cambió de navegador, volvió tras borrar datos del
 * sitio— se vincula sola y se descarga todo.
 */
export async function reanudarSesion(): Promise<Sesion | null> {
  const sesion = await sesionActual();
  if (!sesion) return null;

  const { cuentaVinculada } = await import('./sync');
  const vinculada = await cuentaVinculada();
  if (vinculada === sesion.userId) {
    arrancar();
    void pedirCodigoSiFalta();
  }
  // Con datos de otra cuenta en el dispositivo no se vincula sola: se mezclarían.
  else if (await tieneDatosDeOtraCuenta(sesion.userId)) return null;
  else {
    await vincularCuenta(sesion.userId);
    void pedirCodigoSiFalta();
  }

  return sesion;
}

/**
 * Mensajes en castellano y **sin culpar al usuario**: qué pasó y qué se puede hacer.
 * «Credenciales inválidas» no es ninguna de las dos cosas.
 */
export function mensajeDeError(
  error: unknown,
  contexto: 'entrar' | 'recuperar' = 'entrar',
): string {
  if (error instanceof ErrorDeApi) {
    if (error.status === 401 || error.status === 403) {
      return contexto === 'recuperar'
        ? 'Ese usuario y ese código no coinciden. Revisa el código que guardaste.'
        : 'Ese usuario y esa contraseña no coinciden.';
    }
    if (error.code === 'USERNAME_IS_ALREADY_TAKEN' || error.status === 409) {
      return 'Ese usuario ya existe. Prueba otro.';
    }
    if (error.status === 429) return 'Demasiados intentos. Espera 15 minutos y vuelve a probar.';
    if (error.status === 400 || error.status === 422) {
      return error.message && error.message !== error.code
        ? error.message
        : 'Revisa el usuario y la contraseña (al menos 10 caracteres).';
    }
    return 'El servidor no responde. No es tu contraseña: inténtalo en un rato.';
  }
  return 'Sin conexión. Hace falta internet para entrar; inténtalo cuando vuelva la red.';
}

export type LoLocal = { dias: number; entradas: number; sesiones: number; hitos: number };

/** Lo que sube al vincular, contado aquí: es la evidencia de «no se pierde nada». */
export async function loDeEsteDispositivo(): Promise<LoLocal> {
  const [registros, entradas, sesiones, hitos] = await Promise.all([
    db.habitEntries.filter((e) => e.deletedAt === null).toArray(),
    db.journalEntries.filter((e) => e.deletedAt === null).count(),
    db.focusSessions.toArray(),
    db.milestones.count(),
  ]);
  // Una sesión corregida no es una sesión más: la nueva sustituye a la vieja.
  const sustituidas = new Set(sesiones.map((s) => s.supersedesId).filter(Boolean));
  return {
    dias: new Set(registros.map((e) => e.localDate)).size,
    entradas,
    sesiones: sesiones.filter((s) => !sustituidas.has(s.id)).length,
    hitos,
  };
}

/** Mínimo de Better Auth (ver `apps/api/src/auth.ts`). Se dice antes de enviar. */
export const MINIMO_CONTRASENA = 10;
